import type { IconName } from '../components/ui/Icon';

export interface NavItem {
  id: string;
  label: string;
  path: string;
  icon: IconName;
  /** shown when the user holds ANY of these permission codes; empty = everyone */
  perms: string[];
}

/** Menu groups and items from the reference design. Visibility follows permission codes, not role names. */
export const NAV: Array<{ group: string; items: NavItem[] }> = [
  {
    group: 'Overview',
    items: [
      { id: 'dashboard', label: 'Dashboard', path: '/dashboard', icon: 'dash', perms: ['dashboard.executive', 'dashboard.finance', 'dashboard.accounts', 'dashboard.department', 'dashboard.audit', 'dashboard.system'] },
      { id: 'approvals', label: 'Approvals', path: '/approvals', icon: 'inbox', perms: ['approval.act'] },
      { id: 'notifications', label: 'Notifications', path: '/notifications', icon: 'bell', perms: [] },
    ],
  },
  {
    group: 'Procure to pay',
    items: [
      { id: 'prs', label: 'Purchase requests', path: '/purchase-requests', icon: 'pr', perms: ['pr.read'] },
      { id: 'pos', label: 'Purchase orders', path: '/purchase-orders', icon: 'po', perms: ['po.read'] },
      { id: 'invoices', label: 'Vendor invoices', path: '/invoices', icon: 'inv', perms: ['invoice.read'] },
      { id: 'payments', label: 'Payments', path: '/payments', icon: 'pay', perms: ['payment.read'] },
    ],
  },
  {
    group: 'Receivables & cash',
    items: [
      { id: 'receivables', label: 'Receivables', path: '/receivables', icon: 'recv', perms: ['receivable.read'] },
      { id: 'reconciliation', label: 'Bank reconciliation', path: '/reconciliation', icon: 'bank', perms: ['recon.read'] },
      { id: 'journals', label: 'Journal entries', path: '/journal-entries', icon: 'je', perms: ['je.read'] },
    ],
  },
  {
    group: 'Planning & compliance',
    items: [
      { id: 'budgets', label: 'Budgets', path: '/budgets', icon: 'budget', perms: ['budget.read'] },
      { id: 'compliance', label: 'GST & TDS', path: '/compliance', icon: 'tax', perms: ['tax.read'] },
    ],
  },
  {
    group: 'Records',
    items: [
      { id: 'reports', label: 'Reports', path: '/reports', icon: 'report', perms: ['report.financial', 'report.operational', 'report.department', 'report.executive'] },
      { id: 'documents', label: 'Documents', path: '/documents', icon: 'doc', perms: ['doc.read'] },
      { id: 'audit', label: 'Audit log', path: '/audit', icon: 'audit', perms: ['audit.read'] },
    ],
  },
  {
    group: 'Administration',
    items: [
      { id: 'masters', label: 'Masters', path: '/masters', icon: 'master', perms: ['master.read'] },
      { id: 'users', label: 'Users & roles', path: '/users', icon: 'users', perms: ['user.read', 'role.read'] },
      { id: 'workflow', label: 'Approval rules', path: '/workflow', icon: 'flow', perms: ['workflow.read', 'workflow.configure', 'workflow.approve'] },
      { id: 'periods', label: 'Month-end close', path: '/periods', icon: 'lock', perms: ['period.read'] },
      { id: 'integrations', label: 'Integrations', path: '/integrations', icon: 'plug', perms: ['integration.read'] },
      { id: 'settings', label: 'System settings', path: '/settings', icon: 'master', perms: ['settings.manage'] },
    ],
  },
];

export const allNavItems = () => NAV.flatMap((g) => g.items);

/** Bottom tab bar on phones: the four most useful destinations this person may open, in this order of relevance. */
const TAB_PRIORITY = ['dashboard', 'approvals', 'prs', 'invoices', 'payments', 'receivables', 'pos', 'journals'];
/** One-word names that fit under a tab icon. */
export const TAB_LABEL: Record<string, string> = { dashboard: 'Home', approvals: 'Approvals', prs: 'Requests', pos: 'Orders', invoices: 'Invoices', payments: 'Payments', receivables: 'Receivables', journals: 'Journals' };
export const phoneTabs = (allowed: NavItem[]): NavItem[] =>
  TAB_PRIORITY.map((id) => allowed.find((i) => i.id === id)).filter((i): i is NavItem => !!i).slice(0, 4);

/** Title for the phone top bar: the menu item whose path the page lives under. */
export function pageTitleFor(pathname: string): string {
  const hit = allNavItems().find((i) => pathname === i.path || pathname.startsWith(`${i.path}/`));
  if (hit) return hit.label;
  if (pathname.startsWith('/account')) return 'My profile';
  if (pathname.startsWith('/search')) return 'Search';
  return 'MHS Finance';
}
